import { Grid, Box } from '@mui/material';
import MemoryBulbIcon from 'components/SVG/MemoryBulbIcon';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type {
  LoggedInUserFavouriteListMeta,
  LoggedInUserMeta,
} from 'types/common';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import type { DropdownOption } from 'types/common/DropdownOption';
import type { JoinedMemory } from 'types/Memory';
import { SparkEllipsisTypography } from 'sharedComponents/UI';
import SparkBadges from 'components/common/SparkBadges';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import { SPARK_BADGE_TYPES } from 'constants/common';
import { SparkImageLoader } from 'sharedComponents/UI';
import { useAddMemoryRating, useUpdateMemoryRating } from 'hooks/memory';
import type { ReviewFormModalValues } from 'components/common/ReviewFormModal';

type CardHeaderProps = {
  joinedMemory: JoinedMemory | undefined;

  onCreateList?: (values: CreateFavouriteListFormValues) => void;
  onFavouriteItemClick?: (itemID: number) => void;
  isLoading?: boolean | undefined;
  existingFavouriteList?: LoggedInUserFavouriteListMeta[];
  handleSendReport: (values: SparkReportFormValues) => Promise<void>;
  flagReasonsDropdownOptions: DropdownOption[];
  onDelete: () => void;
  onArchive: () => void;
  userPermissions: LoggedInUserMeta['permissions'];
};

const MemoryCardHeader = ({
  joinedMemory,
  flagReasonsDropdownOptions,
  handleSendReport,
  existingFavouriteList,
  isLoading,
  onCreateList,
  onFavouriteItemClick,
  onArchive,
  onDelete,
  userPermissions,
}: CardHeaderProps) => {
  const memoryID = joinedMemory?.id || '';
  const { mutateAsync: addMemoryRating } = useAddMemoryRating(memoryID);

  const { ratings, permissions } = joinedMemory?.meta.user || {};
  const allowedToReview = permissions?.canReview;
  const userRating = ratings?.find((rating) => rating.memoryID === memoryID);

  const { mutateAsync: updateMemoryRating } = useUpdateMemoryRating(
    userRating?.id ? `${userRating?.id}` : undefined,
    memoryID
  );
  const badgesCategoryMap = getGropedSparkBadges(joinedMemory?.meta.badges);

  const handleUpdateMemoryRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addMemoryRating({ ...values, memoryID });
    }
    if (doesReviewExists) {
      await updateMemoryRating({ ...values, memoryID });
    }
  };

  return (
    <Grid
      container
      justifyContent='space-between'
      alignItems='center'
      gap={1}
      sx={(theme) => ({
        padding: '8px 32px',
        paddingTop: 0,
        [theme.breakpoints.down(1000)]: {
          padding: 2,
        },
      })}
    >
      <Grid item>
        <Box
          sx={{
            width: '100px',
            height: '100px',
            background:
              'linear-gradient(45deg, #F4821F, #EF5251, #DD529C, #6268AF)',
            border: '1px solid white',
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: -4,
          }}
        >
          {joinedMemory?.attributes.iconURL ? (
            <SparkImageLoader
              width='100px'
              height='100px'
              style={{ borderRadius: '12px' }}
              src={joinedMemory?.attributes.iconURL}
              alt=''
            />
          ) : (
            <SparkAvatarBackground
              width='100px'
              height='100px'
              rounded={false}
              icon={<MemoryBulbIcon width={'46.84px'} height={'41.64px'} />}
              src={joinedMemory?.attributes.logoURL || ''}
            />
          )}
        </Box>
      </Grid>
      <Grid item container flex={1} direction='column' alignSelf='center'>
        <Box display='flex' alignItems='center' gap={2}>
          <SparkEllipsisTypography
            variant='h5'
            title={joinedMemory?.attributes.name}
            sx={{
              WebkitLineClamp: 1,
              wordBreak: 'keep-all',
            }}
          >
            {joinedMemory?.attributes.name || '- -'}
          </SparkEllipsisTypography>
        </Box>
        <SparkBadges
          badges={badgesCategoryMap?.[SPARK_BADGE_TYPES.Creator] || []}
        />
      </Grid>
      <Grid
        item
        container
        flex={0.3}
        gap={1}
        justifyContent='flex-end'
        flexWrap='nowrap'
      >
        <SparkOptionsModal
          resourceLogoURL={joinedMemory?.attributes.iconURL || ''}
          privacyTypeId={joinedMemory?.attributes.memoryPrivacyTypeID}
          resourceAccessLinkToken={joinedMemory?.attributes.accessLinkToken}
          options={joinedMemory?.meta.options}
          resourceID={joinedMemory?.id}
          handleSparkRatingSave={handleUpdateMemoryRating}
          userRating={userRating}
          allowedToReview={allowedToReview}
          resourceType='memory'
          onFavouriteItemClick={onFavouriteItemClick}
          existingFavouriteList={existingFavouriteList || []}
          onCreateList={onCreateList}
          flagReasonsDropdownOptions={flagReasonsDropdownOptions}
          onSendReport={handleSendReport}
          isResourceFavourite={joinedMemory?.meta.user.isFavourited}
          isLoading={isLoading}
          onDelete={onDelete}
          onArchive={onArchive}
          resourceName={joinedMemory?.attributes?.name || '- -'}
          userPermissions={userPermissions}
        />
      </Grid>
    </Grid>
  );
};

export default MemoryCardHeader;
